import type { IconName } from '@/components/kit';
import { Permission } from '@/types/user';

/**
 * The nine role surfaces, exactly as the desktop canvases draw them.
 *
 * EXTRACTED, NOT AUTHORED. Every door, icon, overline, sub-line and foot
 * identity below was read out of the rendered `.dc.html` boards in
 * `3 Canvases — open these/Desktop`, and the icon names were recovered by
 * matching each board's embedded SVG path against `icons.data.ts` — so they are
 * the same 123 Tabler glyphs the design ships, not a re-guess. Change this file
 * only to follow a canvas change.
 *
 * WHY IT IS ONE FILE. §13.3 and §11.6 both say the rail and its foot are built
 * once and configured per role; five roles specced the account menu
 * independently and drifted. Reading the shape from data keeps a role's doors
 * in one reviewable place instead of nine page components.
 *
 * WHAT THIS IS NOT: a permission model. It says what a role's navigation LOOKS
 * like, which is a presentation concern. Authorisation lives on the backend in
 * `src/common/constants/role-names.ts`, whose allow-lists fail closed. See the
 * `backendRole` field — two of these nine have no backend role at all.
 */

export interface DesignDoor {
  key: string;
  label: string;
  icon: IconName;
  /**
   * Where this door actually goes in the product.
   *
   * §13.3 calls a rail item that leads nowhere "the single most common defect
   * in the set", so a door either has a route or the shell has to say it is not
   * built. Absent here means the latter — never a silent no-op.
   *
   * The design's door set and the product's route set are not the same shape
   * (a Doctor has five doors against a dozen routes), which is exactly the
   * reconciliation docs/ROLE-MODEL.md is about. This is where the two meet.
   */
  route?: string;
  /**
   * Other path prefixes this door is the parent of.
   *
   * The boards light Today while you are on Results or Patient messages, and
   * Patients while you are in an encounter — because that is where you came
   * from. Without it the rail goes dark the moment you open anything, which
   * reads as "you have left the app".
   */
  activeOn?: string[];
  /**
   * The second line, where a role's rail carries live state. Reception is the
   * only canvas that uses it on every door. It is a COUNT or a contents list,
   * never a description — "now #7 · 5 waiting · 2 not arrived".
   */
  sub?: string;
  /** A count pinned right — Reception's Messages centre. */
  badge?: string;
  /**
   * Permission the person must hold for this door to be drawn.
   *
   * §13.3: a rail item that leads nowhere is the single most common defect.
   * A door to a surface the backend refuses is that defect with an extra step
   * — the page loads, then empties, or answers 403.
   */
  requires?: Permission;
  /**
   * A static overline printed ABOVE this door, grouping the ones that follow.
   * The Accountant's three are the only ones in the set. Not a role group:
   * nothing folds, nothing clicks (`.t-ovl` uppercases them in CSS, so they are
   * stored in the canvas's own sentence case).
   */
  head?: string;
}

/**
 * A SECOND HAT, folded. §10's corrected multi-role law: the primary role's
 * doors stay ungrouped at the top and the extra role folds beneath it — never
 * a role switcher, never two logins.
 *
 * The Nurse is the case that proves the shape: she carries TWO extra hats,
 * numbered ① and ② in the canvas, so the caret rows have to stack rather than
 * collapse into one "other roles" drawer.
 */
export interface DesignRoleGroup {
  /** Carries the ordinal when a role has more than one — "Stock manager ①". */
  head: string;
  /** The one line shown while folded. Quoted from the canvas. */
  caption: string;
  /** Which role's doors these are, so the group can inherit that role's rail. */
  role: DesignRoleId;
  /** Print the caption as drawn, without recounting its "N doors". */
  literalCaption?: boolean;
  /**
   * Permissions the person must hold for this hat to appear at all.
   *
   * A second hat is not decoration — every door under it reaches a surface the
   * backend gates. Drawing the fold for someone who holds none of them offers
   * doors that answer 403, which is §13.3's defect wearing a different coat.
   * Any ONE of these is enough: the hat is a place to start, not a claim that
   * every door inside is open.
   */
  requires?: Permission[];
}

export type DesignRoleId =
  | 'doctor' | 'clinic-manager' | 'reception' | 'accountant'
  | 'nurse' | 'technician' | 'stock-manager' | 'directory' | 'patient-portal';

export interface DesignRole {
  id: DesignRoleId;
  /** The label the rail foot prints after the person's name. */
  label: string;
  /** §13.3's roster. Every mockup got this wrong — "Dr. Mostafa" in six of nine. */
  person: { name: string; initials: string } | null;
  /**
   * The branch line in the rail foot, and a real scope signal rather than
   * decoration: the money and oversight roles see EVERY branch, the ones who
   * work a room see only theirs.
   */
  scope: 'branch' | 'all-branches';
  doors: DesignDoor[];
  groups?: DesignRoleGroup[];
  /**
   * The tenant-visible backend role that grants this surface, or `null` where
   * the design describes a role the backend does not have. `null` is the
   * finding, not an omission — see ROLE-GAP below.
   */
  backendRole: string | null;
  /** Which canvas board this was read from. */
  source: string;
}

export const DESIGN_ROLES: Record<DesignRoleId, DesignRole> = {
  doctor: {
    id: 'doctor',
    label: 'Doctor',
    person: { name: 'Dr. Mostafa Adam', initials: 'MA' },
    scope: 'branch',
    backendRole: 'Doctor',
    source: 'maie-desktop-doctor/g_today.dc.html',
    doors: [
      { key: 'today', label: 'Today', icon: 'home', route: '/today',
        activeOn: ['/results', '/doctor/messages'] },
      { key: 'queue', label: 'Queue', icon: 'layout-list', route: '/queue' },
      { key: 'patients', label: 'Patients', icon: 'users', route: '/patients',
        activeOn: ['/encounter'] },
      { key: 'library', label: 'Library', icon: 'book', route: '/library' },
      { key: 'directory', label: 'Directory', icon: 'address-book', route: '/directory' },
    ],
    /* ONE LOGIN, TWO HATS — for the people who actually wear both.
       The canvas's Dr. Mostafa Adam owns his clinic, so his rail folds the
       manager's doors beneath his own. An employed doctor does not, and
       drawing Reports / Audit log / System Settings for them offers five doors
       that answer 403. `requires` is the gate: the hat appears only for
       someone who holds the permissions behind it. */
    groups: [{
      head: 'Clinic manager',
      caption: '5 doors, folded — the head doctor’s other hat',
      role: 'clinic-manager',
      /* HOSPITAL_SETTINGS_READ, NOT REPORT_READ. The first gate here named
         report:read and audit:read, which every Doctor holds — so the hat
         appeared on plain doctors, System Settings included, and the URL
         behind it rendered. Only the Owner and the Clinic Manager hold
         hospital_settings:read, which is the permission the five doors
         actually spend. */
      requires: [Permission.HOSPITAL_SETTINGS_READ],
    }],
  },

  'clinic-manager': {
    id: 'clinic-manager',
    label: 'Clinic manager',
    person: { name: 'Dr. Mostafa Adam', initials: 'MA' },
    scope: 'all-branches',
    backendRole: 'Clinic Manager',
    source: 'maie-desktop-clinic-manager/Main.dc.html',
    doors: [
      /* THE CLINIC'S DAY, NOT THE DOCTOR'S. `/home` resolves by ROLE, and for
         a doctor that is their own Today — so under the doctor's folded
         manager hat this door led straight back to the door above it, both
         rail items lit at once. `/home?scope=clinic` is not the answer either:
         it needs the nav-diet platform flag as well as report:read, so on a
         tenant without that flag it silently falls through to the same place.
         `/dashboard` IS the clinic-scope day, and it is a real registered
         page. */
      { key: 'today', label: 'Today', icon: 'home', route: '/dashboard',
        requires: Permission.REPORT_READ },
      { key: 'alerts', label: 'Alerts & approvals', icon: 'bell', route: '/notifications' },
      /* NO ROUTE, AND SAID SO. `/reports` is commented out in the router, so
         this door fell through to the catch-all and landed the manager on the
         DOCTOR'S Today — the URL quietly rewritten, no error, no explanation.
         A door marked "soon" that does not move is honest; one that teleports
         you somewhere else is not. Restore the route here when the page
         returns. */
      /* `/reports` is still not a route, but the manager's own Reports home is
         (`doorRoutes.reports` in pages/roles/clinic-manager). The badge said
         "soon" while the screen existed. */
      { key: 'reports', label: 'Reports', icon: 'chart-bar',
        requires: Permission.REPORT_READ },
      /* `audit_logs:read`, NOT `audit:read`. The resource is `audit_logs`;
         `audit:read` is a name no role is granted, so this door was filtered
         out of every real manager's rail and the audit log was reachable only
         by typing the URL. */
      { key: 'audit', label: 'Audit log', icon: 'history', route: '/audit-log',
        requires: Permission.AUDIT_LOGS_READ },
      { key: 'settings', label: 'System Settings', icon: 'settings', route: '/settings',
        requires: Permission.HOSPITAL_SETTINGS_READ,
        /* Directory is one of Settings' thirteen doors (g_dir). */
        activeOn: ['/directory'] },
    ],
  },

  /**
   * ONE PERSON, TWO JOBS, NO FOLD. Reception's foot reads "Reception · Billing"
   * — not a primary role with billing folded beneath it, the way the Doctor
   * carries the manager. The canvas treats front desk and money as one desk,
   * and Billing is a DOOR rather than a second hat. The backend keeps them as
   * two roles (Receptionist, Billing), which is the mismatch worth deciding on.
   */
  reception: {
    id: 'reception',
    label: 'Reception · Billing',
    person: { name: 'Salma Adel', initials: 'SA' },
    scope: 'branch',
    backendRole: 'Receptionist',
    source: 'maie-desktop-reception/g_avail.dc.html',
    doors: [
      /* EVERY DESK DOOR CARRIES A SECOND LINE (§13.3) — a count or a contents
         list, never a description. The lists are here; the counts are data and
         come from `layouts/railSubs.ts`, which fills queue, patients and
         messages at runtime. Without them the rail was six bare labels against
         a canvas that tells the desk what is waiting before they open it. */
      { key: 'queue', label: 'Queue', icon: 'layout-list', route: '/queue' },
      { key: 'schedule', label: 'Schedule', icon: 'calendar', route: '/schedule',
        sub: 'the built day list · availability' },
      { key: 'patients', label: 'Patients', icon: 'users', route: '/patients' },
      { key: 'billing', label: 'Billing', icon: 'cash', route: '/billing',
        sub: 'Overview · Bills · Claims · Catalog · Insurers' },
      { key: 'directory', label: 'Directory', icon: 'address-book', route: '/directory',
        sub: 'doctors · labs · imaging' },
      { key: 'messages', label: 'Messages centre', icon: 'message-circle', route: '/inbox' },
    ],
  },

  accountant: {
    id: 'accountant',
    label: 'Accountant',
    person: { name: 'Nourhan Adel', initials: 'NA' },
    scope: 'all-branches',
    backendRole: 'Accountant',
    source: 'maie-desktop-accountant/Main.dc.html',
    doors: [
      { key: 'accounts', label: 'Accounts', icon: 'calculator' },
      { key: 'bills', label: 'Bills & claims', icon: 'file-invoice', head: 'Money in' },
      { key: 'receivables', label: 'Receivables', icon: 'clock-dollar' },
      { key: 'expenses', label: 'Expenses', icon: 'receipt-2', head: 'Money out' },
      { key: 'people', label: 'People we pay', icon: 'users-group' },
      { key: 'payables', label: 'Payables', icon: 'building-store' },
      { key: 'tax', label: 'Tax report', icon: 'receipt-tax', head: 'The books' },
      { key: 'pnl', label: 'Profit & loss', icon: 'scale' },
      { key: 'reports', label: 'Financial reports', icon: 'chart-bar' },
      { key: 'export', label: 'Export center', icon: 'download' },
    ],
  },

  nurse: {
    id: 'nurse',
    label: 'Nurse',
    person: { name: 'Hoda Samir', initials: 'HS' },
    scope: 'branch',
    backendRole: 'Nurse',
    source: 'maie-desktop-nurse/Main.dc.html',
    doors: [
      { key: 'myday', label: 'My day', icon: 'list-check', route: '/home' },
      { key: 'stock', label: 'Stock', icon: 'packages', route: '/pharmacy' },
    ],
    groups: [
      { head: 'Stock manager ①', caption: '7 doors, folded — the nurse’s second hat', role: 'stock-manager' },
      { head: 'Technician ②', caption: 'Worklist · My day · Stock — 3 doors, folded', role: 'technician' },
    ],
  },

  technician: {
    id: 'technician',
    label: 'Technician',
    person: { name: 'Tarek Nabil', initials: 'TN' },
    scope: 'branch',
    backendRole: 'Technician',
    source: 'maie-desktop-technician/Main.dc.html',
    doors: [
      { key: 'worklist', label: 'Worklist', icon: 'list-check' },
      { key: 'myday', label: 'My day', icon: 'chart-bar' },
      { key: 'stock', label: 'Stock', icon: 'packages' },
    ],
  },

  'stock-manager': {
    id: 'stock-manager',
    label: 'Stock manager',
    person: { name: 'Hoda Samir', initials: 'HS' },
    scope: 'branch',
    backendRole: 'Stock Manager',
    source: 'maie-desktop-stock-manager/Main.dc.html',
    /* READING THE SHELF AND CHANGING IT ARE DIFFERENT DOORS. `stock:read` is
       deliberately the wider grant — a nurse checks whether the drops are
       there before a round, a technician before a scan — while receiving,
       counting, ordering and transferring all move a number and ask for
       `stock:write`. Without these the fold would show a nurse five doors that
       answer 403. */
    doors: [
      { key: 'stock', label: 'Stock', icon: 'packages', requires: Permission.STOCK_READ },
      { key: 'catalog', label: 'Catalog', icon: 'list-details', requires: Permission.STOCK_READ },
      { key: 'receive', label: 'Receive', icon: 'download', requires: Permission.STOCK_WRITE },
      { key: 'orders', label: 'Orders', icon: 'truck-delivery', requires: Permission.STOCK_WRITE },
      { key: 'count', label: 'Count', icon: 'checkbox', requires: Permission.STOCK_WRITE },
      { key: 'transfers', label: 'Transfers', icon: 'arrows-exchange', requires: Permission.STOCK_WRITE },
      { key: 'directory', label: 'Directory', icon: 'address-book', route: '/directory' },
    ],
    /* THE CANVAS'S HODA IS A NURSE WHO ALSO KEEPS THE SHELF, and it draws her
       nursing doors folded beneath. A real Stock Manager is not a nurse — the
       role holds no patient, appointment or records permission at all — so
       without this gate the fold offered two doors that both answer 403.
       Somebody who genuinely wears both hats holds the nurse's permissions
       and still sees it. (§10's corrected multi-role law.) */
    groups: [{
      head: 'Nurse',
      caption: 'My day · Stock (view-only) — 2 doors, folded',
      role: 'nurse',
      requires: [Permission.MEDICAL_RECORDS_READ],
    }],
  },

  /**
   * NOT A ROLE — a surface. §13.3: the Directory's identity is "inherited from
   * whoever opened it", which is why its foot shows the clinic manager here.
   * Whoever holds the oversight role sees these doors; nobody is "a Directory".
   */
  directory: {
    id: 'directory',
    label: 'Clinic manager',
    person: { name: 'Dr. Mostafa Adam', initials: 'MA' },
    scope: 'all-branches',
    backendRole: 'Clinic Manager',
    source: 'maie-desktop-directory/Main.dc.html',
    doors: [
      { key: 'overview', label: 'Overview', icon: 'home' },
      { key: 'reports', label: 'Reports', icon: 'chart-bar' },
      { key: 'money', label: 'Money', icon: 'wallet' },
      { key: 'staff', label: 'Staff', icon: 'users' },
      { key: 'flows', label: 'Flows', icon: 'route' },
      { key: 'directory', label: 'Directory', icon: 'address-book', head: 'Settings' },
    ],
    groups: [{
      head: 'Doctor',
      caption: 'Today · Patients · Queue · Results · Library · Messages — 6 doors',
      role: 'doctor',
      literalCaption: true,
    }],
  },

  /**
   * The portal has NO RAIL AND NO FOOT — no account row, no sign-out, doors
   * only, because the person holding it is a patient rather than staff. Pass no
   * foot rather than faking an empty one (§13.3).
   */
  'patient-portal': {
    id: 'patient-portal',
    label: 'Patient',
    person: null,
    scope: 'branch',
    backendRole: null,
    source: 'maie-desktop-patient-portal/Main.dc.html',
    doors: [],
  },
};

export const DESIGN_ROLE_IDS = Object.keys(DESIGN_ROLES) as DesignRoleId[];

/**
 * ROLE-GAP — CLOSED. Every role the design describes is one the backend can
 * grant. Technician (2026-09-26), Stock Manager and Accountant (2026-09-28)
 * were the three that were not.
 *
 * Accountant: a tenant-visible STAFF seat, all-branches, that reads no
 * clinical data at all. It holds `patient:read` only so an invoice can name
 * who it is for, and is listed in DEMOGRAPHIC_ONLY_PATIENT_ROLES so the
 * clinical fields stay shut. It is the one non-clinical role in
 * UNRESTRICTED_VISIBILITY_ROLES, deliberately: the books are clinic-wide by
 * definition, and a self-scoped accountant would reconcile a fraction of the
 * ledger and call it the total. Payroll moved off `hospital_settings` onto its
 * own `payout` resource at the same time, because the only way to let somebody
 * run payouts was otherwise to hand them the clinic's settings screen — and
 * `payout:approve` is NOT theirs, since the person who assembles a run must
 * not be the one who releases the money (board 6b).
 *
 * Stock Manager: the same shape, with `stock:read|write`, and no patient
 * permission of ANY kind — the only tenant role with none. Suppliers in the
 * Directory opened to `stock:write` alongside it.
 *
 * ADDING ONE IS NOT A LINE IN AN ENUM, and this note stays because the next
 * person will need it. `role-names.ts` classifies every role across five
 * allow-lists — TENANT_VISIBLE, SCRIBE_ELIGIBLE (= CLINICAL, which drives AI
 * seat billing), STAFF, UNRESTRICTED_VISIBILITY and DEMOGRAPHIC_ONLY_PATIENT —
 * and UNRESTRICTED_VISIBILITY deliberately fails closed, so a role omitted
 * there is silently self-scoped. Getting one wrong is a data-exposure bug.
 * `navigation.test.ts` pins each new role's legacy sidebar for the same
 * reason: anything that widens it means the role quietly gained a power.
 *
 * Reception remains a different shape of mismatch: the canvas draws ONE desk
 * labelled "Reception · Billing", while the backend keeps Receptionist and
 * Billing as separate roles with separate seat caps.
 */
export const ROLE_GAP: ReadonlyArray<{ role: DesignRoleId; note: string }> = [];

